import React, { useEffect, useState } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { formatTranslation } from '../../i18n/formatTranslation';
import {
  requestBookingExtension,
  confirmBookingWork,
  disputeBookingWork,
  estimateBalanceDue,
  runningElapsedMs,
  formatDuration,
  formatClockHMS,
  formatInrDisplay,
  estimateLiveMeter,
  confirmDeadlineRemaining,
  decimalHoursToHMS,
  isBookingPaused,
} from '../../services/bookingFlowApi';
import { formatHoursHm } from '../../utils/durationHours';

const cardStyle = {
  marginTop: '10px',
  padding: '12px',
  borderRadius: '10px',
  border: '1px solid rgba(201,168,76,0.25)',
  background: 'rgba(201,168,76,0.06)',
};

const arrowBtn = {
  background: 'rgba(201,168,76,0.1)',
  border: '1px solid rgba(201,168,76,0.3)',
  color: '#c9a84c',
  borderRadius: 4,
  width: 28,
  height: 22,
  cursor: 'pointer',
  fontSize: 10,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 0,
};

const timeDisplay = {
  color: '#e8e0d0',
  fontSize: 22,
  fontWeight: 700,
  minWidth: 40,
  textAlign: 'center',
  lineHeight: 1,
};

const etaHintStyle = {
  color: '#8896a8',
  fontSize: '11px',
  margin: '8px 0 0',
};

function formatIstDateTime(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Asia/Kolkata',
  });
}

function getEstimatedCompletionAt(booking) {
  const bookedH = Number(booking.booked_hours || booking.quantity || 0);
  if (!bookedH) return null;
  const startRaw = booking.dispatched_at
    || booking.machine_started_at
    || booking.owner_approved_at
    || booking.created_at;
  if (!startRaw) return null;
  const start = new Date(startRaw);
  if (Number.isNaN(start.getTime())) return null;
  return new Date(start.getTime() + bookedH * 60 * 60 * 1000);
}

function formatEtaRemaining(remainingMs, ft) {
  if (remainingMs == null) return null;
  const totalMins = Math.max(0, Math.ceil(remainingMs / 60000));
  if (totalMins >= 60) {
    return ft('etaHoursRemaining', { n: Math.ceil(totalMins / 60) });
  }
  return ft('etaMinutesRemaining', { n: totalMins });
}

function StatusBadge({ status }) {
  const colors = {
    Dispatched: { bg: 'rgba(76,175,80,0.15)', border: '#4CAF50', color: '#4CAF50' },
    Extension_Pending: { bg: 'rgba(255,152,0,0.15)', border: '#FF9800', color: '#FF9800' },
    Completing: { bg: 'rgba(156,39,176,0.15)', border: '#9C27B0', color: '#CE93D8' },
    Disputed: { bg: 'rgba(244,67,54,0.15)', border: '#F44336', color: '#F44336' },
    Completed: { bg: 'rgba(158,158,158,0.15)', border: '#9E9E9E', color: '#BDBDBD' },
  };
  const c = colors[status] || { bg: 'rgba(33,150,243,0.15)', border: '#2196F3', color: '#64B5F6' };
  return (
    <span style={{
      background: c.bg,
      border: `1px solid ${c.border}`,
      color: c.color,
      padding: '3px 10px',
      borderRadius: '20px',
      fontSize: '11px',
      fontWeight: 700,
    }}
    >
      {status.replace(/_/g, ' ')}
    </span>
  );
}

export default function BookingFlowV2Card({
  booking,
  onRefresh,
  onRecharge,
  walletBalance,
  viewOnly = false,
  onDownloadInvoice,
}) {
  const { t } = useLanguage();
  const ft = (key, vars) => formatTranslation(t(key), vars);
  const [busy, setBusy] = useState(false);
  const [extHours, setExtHours] = useState(0);
  const [extMinutes, setExtMinutes] = useState(30);
  const [disputeReason, setDisputeReason] = useState('');
  const [showDispute, setShowDispute] = useState(false);
  const [, tick] = useState(0);

  const status = String(booking.status || 'Active');
  const isLegacyActive = status === 'Active' && booking.owner_approved && !booking.machine_started_at;
  const isDispatched = status === 'Dispatched' || (status === 'Active' && booking.machine_started_at);

  useEffect(() => {
    if (status !== 'Completing' && !isDispatched) return undefined;
    const id = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [status, isDispatched]);

  if (booking.booking_type === 'contract') {
    return (
      <div style={cardStyle}>
        <p style={{ color: '#c9a84c', fontSize: '12px', fontWeight: 700, margin: 0 }}>
          Contract booking
        </p>
        <p style={{ color: '#8896a8', fontSize: '11px', margin: '6px 0 0' }}>
          Settlement coming in a future update.
        </p>
      </div>
    );
  }

  const handleExtension = async (hours) => {
    if (busy) return;

    // Calculate advance amount for confirm
    const rate = Number(booking.rate_per_hour || 0);
    const totalAmt = Math.round(hours * rate * 100) / 100;
    const advanceAmt = Math.round(totalAmt * 0.5 * 100) / 100;

    // Show confirm dialog with amount
    const confirmed = window.confirm(
      ft('bf2ExtendConfirm', {
        hours,
        total: totalAmt.toLocaleString('en-IN'),
        advance: advanceAmt.toLocaleString('en-IN'),
      }),
    );

    if (!confirmed) return;

    setBusy(true);
    try {
      await requestBookingExtension(booking.id, hours);
      onRefresh?.();
      alert(
        ft('bf2ExtendSuccess', {
          hours,
          advance: advanceAmt.toLocaleString('en-IN'),
        }),
      );
    } catch (err) {
      if (err.status === 402) {
        const required = err.data?.required
          || err.data?.data?.required;
        alert(ft('walletRechargeRequired', {
          amount: Number(required || 0)
            .toLocaleString('en-IN'),
        }));
        onRecharge?.();
      } else {
        alert(err.message || t('bookingExtensionFailed'));
      }
    } finally {
      setBusy(false);
    }
  };

  const handleConfirm = async () => {
    if (busy) return;
    const due = estimateBalanceDue(booking);
    if (due > walletBalance) {
      alert(ft('walletRechargeRequired', { amount: due.toLocaleString('en-IN') }));
      onRecharge?.();
      return;
    }
    if (!window.confirm(ft('confirmPayBalancePrompt', { amount: due.toLocaleString('en-IN') }))) return;
    setBusy(true);
    try {
      await confirmBookingWork(booking.id);
      onRefresh?.();
    } catch (err) {
      if (err.status === 402) onRecharge?.();
      alert(err.message || t('bookingConfirmFailed'));
    } finally {
      setBusy(false);
    }
  };

  const handleDispute = async () => {
    if (!disputeReason.trim() || busy) return;
    setBusy(true);
    try {
      await disputeBookingWork(booking.id, disputeReason.trim());
      setShowDispute(false);
      onRefresh?.();
    } catch (err) {
      alert(err.message || t('bookingDisputeFailed'));
    } finally {
      setBusy(false);
    }
  };

  if (status === 'Active' && !booking.owner_approved) {
    return (
      <div style={cardStyle}>
        <p style={{ color: '#8896a8', fontSize: '12px', margin: 0 }}>
          {t('bf2AwaitingDispatch')}
        </p>
        <p style={etaHintStyle}>⏳ {t('etaOwnerApproval')}</p>
      </div>
    );
  }

  if (isLegacyActive) {
    return (
      <div style={cardStyle}>
        <p style={{ color: '#8896a8', fontSize: '12px', margin: 0 }}>
          {t('bf2OwnerApprovedSoon')}
        </p>
      </div>
    );
  }

  if (isDispatched) {
    const bookedH = Number(booking.booked_hours || booking.quantity || 3);
    const elapsed = runningElapsedMs(booking);
    const bookedMs = bookedH * 3600000;
    const remaining = Math.max(0, bookedMs - elapsed);
    const meter = estimateLiveMeter(booking);
    const paused = isBookingPaused(booking);
    return (
      <div style={cardStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
          <StatusBadge status="Dispatched" />
          <span style={{ color: paused ? '#FF9800' : '#4CAF50', fontSize: '11px', fontWeight: 700 }}>
            {paused ? '⏸ PAUSED' : `🟢 ${t('bf2Active')}`}
          </span>
        </div>
        {booking.operator && (
          <div style={{
            background: 'rgba(201,168,76,0.08)',
            border: '1px solid rgba(201,168,76,0.2)',
            borderRadius: 8,
            padding: '8px 12px',
            marginBottom: 8,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
          >
            <div>
              <p style={{
                color: '#e8e0d0',
                fontSize: 12,
                fontWeight: 600,
                margin: 0,
              }}
              >
                👷 {booking.operator.name.split(' ')[0]}
              </p>
              {booking.operator.experience_years > 0 && (
                <p style={{
                  color: '#8896a8',
                  fontSize: 11,
                  margin: '2px 0 0',
                }}
                >
                  {booking.operator.experience_years} yr exp
                  {booking.operator.experience_machine_types?.length > 0
                    ? ` · ${booking.operator.experience_machine_types.slice(0, 2).join(', ')}`
                    : ''}
                </p>
              )}
            </div>
            {(() => {
              const exp = Number(booking.operator.experience_years || 0);
              if (exp >= 5) {
                return (
                  <span style={{ color: '#c9a84c', fontSize: 11 }}>⭐⭐⭐</span>
                );
              }
              if (exp >= 2) {
                return (
                  <span style={{ color: '#e2c97e', fontSize: 11 }}>⭐⭐</span>
                );
              }
              if (exp >= 1) {
                return (
                  <span style={{ color: '#8896a8', fontSize: 11 }}>⭐</span>
                );
              }
              return null;
            })()}
          </div>
        )}
        <div style={{
          textAlign: 'center',
          margin: '8px 0 10px',
          padding: '10px 8px',
          borderRadius: '8px',
          background: paused ? 'rgba(255,152,0,0.08)' : 'rgba(0,0,0,0.25)',
          border: paused ? '1px solid rgba(255,152,0,0.35)' : '1px solid rgba(201,168,76,0.2)',
        }}
        >
          <p style={{
            color: paused ? '#FF9800' : '#c9a84c',
            fontSize: '28px',
            fontWeight: 800,
            margin: '0 0 4px',
            fontVariantNumeric: 'tabular-nums',
            letterSpacing: '0.06em',
          }}
          >
            {formatClockHMS(elapsed)}
          </p>
          <p style={{ color: '#8896a8', fontSize: '10px', margin: 0, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            {t('bf2MachineHoursLive')}
          </p>
        </div>
        <p style={{ color: '#e8e0d0', fontSize: '12px', margin: '0 0 4px' }}>
          {t('bf2Booked')}: {formatHoursHm(bookedH)}
        </p>
        <p style={{ color: remaining < 1800000 ? '#FF9800' : '#8896a8', fontSize: '11px', margin: '0 0 8px' }}>
          {t('bf2Remaining')}: {formatDuration(remaining)}
        </p>
        {(() => {
          const etaAt = getEstimatedCompletionAt(booking);
          const etaLabel = etaAt ? formatIstDateTime(etaAt.toISOString()) : null;
          if (!etaLabel) return null;
          return (
            <p style={{ ...etaHintStyle, margin: '0 0 8px' }}>
              ⏱️ {t('etaWorkInProgress')}: {etaLabel}
            </p>
          );
        })()}
        <p style={{ color: '#e8e0d0', fontSize: '12px', margin: '0 0 2px' }}>
          {t('bf2RunningBill')}: ₹{formatInrDisplay(meter.gross)}
        </p>
        <p style={{ color: '#c9a84c', fontSize: '13px', fontWeight: 700, margin: '0 0 4px' }}>
          {t('bf2Outstanding')}: ₹{formatInrDisplay(meter.outstanding)}
        </p>
        {meter.minimumApplied && meter.hasHourlyRate && (
          <p style={{ color: '#8896a8', fontSize: '10px', margin: '0 0 8px' }}>
            {ft('bf2MinimumBilling', {
              hours: meter.minH,
              amount: formatInrDisplay(meter.minH * meter.rate),
            })}
          </p>
        )}
        <p style={{ color: '#4CAF50', fontSize: '11px', margin: '0 0 10px' }}>
          {t('bf2AdvancePaid')}: ₹{formatInrDisplay(booking.advance_paid || 0)} ✅
        </p>
        {booking.machine_started_at && (
          <p style={{ color: '#8896a8', fontSize: '10px', margin: '0 0 10px' }}>
            {t('bf2Started')}: {new Date(booking.machine_started_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
          </p>
        )}
        {!viewOnly && (
          <div style={{ marginTop: 8 }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              marginBottom: 8,
              flexWrap: 'wrap',
            }}
            >
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
              }}
              >
                <button
                  type="button"
                  style={arrowBtn}
                  onClick={() => setExtHours((h) => Math.min(23, h + 1))}
                >
                  ▲
                </button>
                <span style={timeDisplay}>
                  {String(extHours).padStart(2, '0')}
                </span>
                <button
                  type="button"
                  style={arrowBtn}
                  onClick={() => setExtHours((h) => Math.max(0, h - 1))}
                >
                  ▼
                </button>
              </div>

              <span style={{
                color: '#c9a84c',
                fontSize: 20,
                fontWeight: 700,
              }}
              >
                :
              </span>

              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
              }}
              >
                <button
                  type="button"
                  style={arrowBtn}
                  onClick={() => setExtMinutes((m) => (m >= 55 ? 0 : m + 5))}
                >
                  ▲
                </button>
                <span style={timeDisplay}>
                  {String(extMinutes).padStart(2, '0')}
                </span>
                <button
                  type="button"
                  style={arrowBtn}
                  onClick={() => setExtMinutes((m) => (m <= 0 ? 55 : m - 5))}
                >
                  ▼
                </button>
              </div>

              <span style={{
                color: '#8896a8',
                fontSize: 11,
              }}
              >
                hrs : min
              </span>
            </div>

            {(extHours > 0 || extMinutes > 0) && (() => {
              const decHrs = extHours + extMinutes / 60;
              const rate = Number(booking.rate_per_hour || 0);
              const total = Math.round(decHrs * rate * 100) / 100;
              const advance = Math.round(total * 0.5 * 100) / 100;
              return (
                <p style={{
                  color: '#8896a8',
                  fontSize: 11,
                  margin: '0 0 8px',
                }}
                >
                  ₹
                  {total.toLocaleString('en-IN')}
                  {' '}
                  total · ₹
                  {advance.toLocaleString('en-IN')}
                  {' '}
                  advance
                </p>
              );
            })()}

            <button
              type="button"
              disabled={busy || (extHours === 0 && extMinutes === 0)}
              onClick={() => {
                const decHrs = extHours + extMinutes / 60;
                if (decHrs < 0.083) return;
                handleExtension(
                  Math.round(decHrs * 1000) / 1000,
                );
              }}
              style={{
                background: (extHours === 0 && extMinutes === 0)
                  ? 'rgba(201,168,76,0.05)'
                  : 'rgba(201,168,76,0.15)',
                border: '1px solid rgba(201,168,76,0.4)',
                color: '#c9a84c',
                borderRadius: '6px',
                padding: '8px 16px',
                fontSize: '12px',
                cursor: (busy || (extHours === 0 && extMinutes === 0))
                  ? 'not-allowed'
                  : 'pointer',
                width: '100%',
              }}
            >
              {busy
                ? '...'
                : `Extend ${String(extHours).padStart(2, '0')}:${String(extMinutes).padStart(2, '0')}`}
            </button>
          </div>
        )}
      </div>
    );
  }

  if (status === 'Extension_Pending') {
    return (
      <div style={cardStyle}>
        <StatusBadge status="Extension_Pending" />
        <p style={{ color: '#FF9800', fontSize: '12px', margin: '8px 0 0' }}>
          {t('bf2ExtensionPending')}
        </p>
      </div>
    );
  }

    if (status === 'Completing' || status === 'Completing_PendingPayment') {
    // If client already confirmed + paid,
    // show processing message instead of Balance Due
    if (booking.client_confirmed_at) {
      return (
        <div style={cardStyle}>
          <StatusBadge status="Completing" />
          <p style={{
            color: '#4caf50',
            fontSize: '12px',
            margin: '8px 0 0',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }}>
            ✓ Payment received — settlement processing.
            Contact admin if this persists.
          </p>
        </div>
      );
    }
    const minH = Number(booking.minimum_hours || 3);
    const logged = Number(booking.operator_actual_hours || 0);
    const loggedHMS = decimalHoursToHMS(logged);
    const finalH = Math.max(logged, minH);
    const due = estimateBalanceDue(booking);
    const remaining = confirmDeadlineRemaining(booking);
    const mins = remaining != null ? Math.floor(remaining / 60000) : null;
    const secs = remaining != null ? Math.floor((remaining % 60000) / 1000) : null;
    const deadlineTime = mins != null ? `${mins}:${String(secs).padStart(2, '0')}` : '';

    return (
      <div style={cardStyle}>
        <p style={{ color: '#CE93D8', fontWeight: 700, fontSize: '13px', margin: '0 0 8px' }}>
          ⚠️ {t('bf2ConfirmWorkComplete')}
        </p>
        <p style={{ color: '#e8e0d0', fontSize: '12px', margin: '0 0 4px' }}>
          {ft('bf2OperatorLogged', {
            logged: `${loggedHMS} (${logged} hrs)`,
            min: minH,
            final: finalH,
          })}
        </p>
        <p style={{ color: '#c9a84c', fontSize: '12px', margin: '0 0 8px' }}>
          {ft('bf2BalanceDue', { amount: due.toLocaleString('en-IN') })}
        </p>
        {mins != null && (
          <p style={{ color: '#FF9800', fontSize: '11px', margin: '0 0 10px' }}>
            ⏰ {ft('bf2Deadline', { time: deadlineTime })}
          </p>
        )}
        {remaining != null && (
          <p style={{ ...etaHintStyle, color: '#FF9800', margin: '0 0 10px' }}>
            💳 {t('etaPaymentDeadline')}: {formatEtaRemaining(remaining, ft)}
          </p>
        )}
        {!viewOnly && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          <button
            type="button"
            disabled={busy}
            onClick={handleConfirm}
            style={{
              background: 'rgba(76,175,80,0.2)',
              border: '1px solid #4CAF50',
              color: '#4CAF50',
              borderRadius: '6px',
              padding: '8px 12px',
              fontSize: '11px',
              cursor: busy ? 'wait' : 'pointer',
            }}
          >
            ✅ {ft('bf2ConfirmPayBtn', { amount: due.toLocaleString('en-IN') })}
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => setShowDispute(!showDispute)}
            style={{
              background: 'rgba(244,67,54,0.1)',
              border: '1px solid rgba(244,67,54,0.4)',
              color: '#F44336',
              borderRadius: '6px',
              padding: '8px 12px',
              fontSize: '11px',
              cursor: 'pointer',
            }}
          >
            ❌ {t('bf2Dispute')}
          </button>
        </div>
        )}
        {!viewOnly && showDispute && (
          <div style={{ marginTop: '10px' }}>
            <textarea
              value={disputeReason}
              onChange={(e) => setDisputeReason(e.target.value)}
              placeholder={t('bf2DisputePlaceholder')}
              rows={2}
              style={{ width: '100%', background: '#0a1628', border: '1px solid rgba(201,168,76,0.2)', color: '#e8e0d0', borderRadius: '6px', padding: '8px', fontSize: '12px' }}
            />
            <button type="button" disabled={busy} onClick={handleDispute} style={{ marginTop: '6px', padding: '6px 12px', cursor: 'pointer', background: 'rgba(244,67,54,0.15)', border: '1px solid #F44336', color: '#F44336', borderRadius: '6px' }}>
              {t('bf2SubmitDispute')}
            </button>
          </div>
        )}
      </div>
    );
  }

  if (status === 'Disputed') {
    return (
      <div style={{ ...cardStyle, borderColor: 'rgba(244,67,54,0.4)' }}>
        <StatusBadge status="Disputed" />
        <p style={{ color: '#F44336', fontSize: '12px', margin: '8px 0 0' }}>
          {t('bf2DisputeRaised')}
        </p>
        <p style={etaHintStyle}>⚠️ {t('etaDisputeReview')}</p>
        {booking.dispute_reason && (
          <p style={{ color: '#8896a8', fontSize: '11px', margin: '4px 0 0' }}>{booking.dispute_reason}</p>
        )}
      </div>
    );
  }

  if (status === 'Completed' || status.toLowerCase() === 'completed') {
    const total = Number(booking.gross_amount_final || booking.total_amount || 0);
    const advance = Number(booking.advance_paid || 0);
    return (
      <div style={cardStyle}>
        <StatusBadge status="Completed" />
        {booking.operator_completed_at && (
          <p style={etaHintStyle}>
            ✅ {t('etaWorkCompleted')} {formatIstDateTime(booking.operator_completed_at)}
          </p>
        )}
        <p style={{ color: '#e8e0d0', fontSize: '12px', margin: '8px 0 0' }}>
          {ft('bf2CompletedSummary', {
            total: total.toLocaleString('en-IN'),
            advance: advance.toLocaleString('en-IN'),
          })}
          {booking.final_hours ? ft('bf2CompletedHours', { hours: booking.final_hours }) : ''}
        </p>
        {typeof onDownloadInvoice === 'function' && (
          <button
            type="button"
            onClick={() => onDownloadInvoice(booking)}
            style={{
              width: '100%',
              marginTop: '10px',
              padding: '10px',
              background: 'rgba(201,168,76,0.12)',
              border: '1px solid rgba(201,168,76,0.4)',
              color: '#c9a84c',
              borderRadius: '8px',
              fontWeight: 700,
              fontSize: '12px',
              cursor: 'pointer',
            }}
          >
            {String.fromCodePoint(0x1F4C4)} {t('downloadInvoice')}
          </button>
        )}
      </div>
    );
  }

  return null;
}
